import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, Check, Sparkles } from "lucide-react";

const AphroditesWaitlist = () => {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
    const [message, setMessage] = useState("");
    const [count, setCount] = useState(null);

    useEffect(() => {
        api.get("/waitlist/aphrodites/count")
            .then((r) => setCount(r.data.count ?? 0))
            .catch(() => setCount(null));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus("loading");
        setMessage("");
        try {
            const { data } = await api.post("/waitlist/aphrodites", {
                email: email.trim(),
                name: name.trim() || null,
                source: "aphrodites-roots",
            });
            setCount(data.count ?? count);
            setStatus(data.already_subscribed ? "already" : "success");
            setMessage(data.message || "");
            if (!data.already_subscribed) {
                setEmail("");
                setName("");
            }
        } catch (err) {
            setStatus("error");
            const detail = err?.response?.data?.detail;
            setMessage(
                Array.isArray(detail)
                    ? "Please enter a valid email address."
                    : detail || "Something went wrong — please try again."
            );
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="waitlist-success"
                className="max-w-xl mx-auto bg-hl-bg/10 border border-[#E8D9B5]/30 rounded-sm px-8 py-9 text-center backdrop-blur-sm"
            >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E8D9B5]/20 mb-4">
                    <Check className="w-5 h-5 text-[#E8D9B5]" strokeWidth={1.8} />
                </div>
                <p className="font-script text-3xl text-[#E8D9B5] leading-none">welcome —</p>
                <p className="mt-3 font-serif text-2xl md:text-3xl text-hl-bg leading-tight">
                    You're on the list.
                </p>
                <p className="mt-4 text-hl-bg/75 text-sm md:text-base leading-relaxed">
                    We'll send a single email when the first batch of wild oregano is
                    picked, dried, and ready to ship. No spam, no weekly newsletter.
                </p>
                {count !== null ? (
                    <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-[#E8D9B5]/70">
                        You're #{count} on the list
                    </p>
                ) : null}
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            data-testid="waitlist-form"
            className="max-w-xl mx-auto"
        >
            <div className="grid sm:grid-cols-[1fr_auto] gap-3">
                <div className="flex flex-col gap-3">
                    <input
                        data-testid="waitlist-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your first name (optional)"
                        autoComplete="given-name"
                        className="w-full px-5 py-4 bg-hl-bg/10 border border-[#E8D9B5]/30 rounded-sm text-hl-bg placeholder:text-hl-bg/45 font-serif text-base focus:outline-none focus:border-[#E8D9B5]/70 transition-colors"
                    />
                    <input
                        data-testid="waitlist-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email"
                        autoComplete="email"
                        className="w-full px-5 py-4 bg-hl-bg/10 border border-[#E8D9B5]/30 rounded-sm text-hl-bg placeholder:text-hl-bg/45 font-serif text-base focus:outline-none focus:border-[#E8D9B5]/70 transition-colors"
                    />
                </div>
                <button
                    data-testid="waitlist-submit"
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-hl-secondary text-hl-bg hover:bg-hl-secondaryDeep hl-btn-lift text-[11px] uppercase tracking-[0.3em] disabled:opacity-60 disabled:cursor-not-allowed self-end"
                >
                    {status === "loading" ? "Sending…" : "Notify me"}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
            </div>

            {status === "already" ? (
                <p
                    data-testid="waitlist-already"
                    className="mt-5 text-sm text-[#E8D9B5]/85 text-center"
                >
                    <Check className="inline w-3.5 h-3.5 mr-2 -mt-0.5" strokeWidth={2} />
                    {message || "You're already on the list."}
                </p>
            ) : null}

            {status === "error" ? (
                <p
                    data-testid="waitlist-error"
                    className="mt-5 text-sm text-red-300/90 text-center"
                >
                    {message}
                </p>
            ) : null}

            {count !== null && count > 0 && status !== "already" ? (
                <p
                    data-testid="waitlist-count"
                    className="mt-7 text-[10px] uppercase tracking-[0.3em] text-[#E8D9B5]/70 text-center inline-flex items-center justify-center gap-2 w-full"
                >
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                    {count.toLocaleString()} {count === 1 ? "person is" : "people are"} already waiting for the first batch
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                </p>
            ) : null}
        </form>
    );
};

export default AphroditesWaitlist;
